import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  FilterList,
  Build,
  Warning,
  CheckCircle,
  Schedule,
  Cancel,
  Pause,
  TrendingUp,
  TrendingDown,
  Person,
} from '@mui/icons-material';
import axios from 'axios';
import { mockApi } from '../../services/mockData';

interface ServiceRequest {
  _id: string;
  customer: {
    id: string;
    name: string;
    contactPerson: {
      firstName: string;
      lastName: string;
      title?: string;
    };
    email: string;
    phone: string;
  };
  project: {
    id: string;
    name: string;
    completedDate: string;
  };
  installer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  issueType: string;
  priority: string;
  status: string;
  description: string;
  reportedDate: string;
  scheduledDate?: string;
  completedDate?: string;
  estimatedDuration: number;
  actualDuration?: number;
  location: string;
  assignedTechnician: {
    id: string;
    name: string;
    email: string;
  };
  resolution?: string;
  partsNeeded: string[];
  warrantyCovered: boolean;
  customerSatisfaction?: number;
  createdAt: string;
  updatedAt: string;
}

interface InstallerPerformance {
  installerId: string;
  installerName: string;
  totalInstallations: number;
  totalServiceRequests: number;
  completedServiceRequests: number;
  openServiceRequests: number;
  inProgressServiceRequests: number;
  cancelledServiceRequests: number;
  averageResolutionTime: number;
  customerSatisfactionScore: number;
  warrantyIssues: number;
  malfunctionIssues: number;
  maintenanceRequests: number;
  upgradeRequests: number;
  serviceRequestRate: number;
  resolutionEfficiency: number;
}

const Services: React.FC = () => {
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [installerPerformance, setInstallerPerformance] = useState<InstallerPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState('');
  const [installerFilter, setInstallerFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState<ServiceRequest | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();

  const fetchServiceRequests = useCallback(async () => {
    try {
      setLoading(true);
      const services = await mockApi.getServices();
      setServiceRequests(services as unknown as ServiceRequest[]);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch service requests');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, priorityFilter, issueTypeFilter, installerFilter]);

  const fetchInstallerPerformance = useCallback(async () => {
    try {
      // Mock installer performance data
      const mockPerformance = [
        { installer: 'John Doe', totalInstalls: 25, serviceRequests: 3, performance: 92 },
        { installer: 'Jane Smith', totalInstalls: 18, serviceRequests: 1, performance: 95 },
        { installer: 'Mike Johnson', totalInstalls: 22, serviceRequests: 5, performance: 78 },
        { installer: 'Sarah Wilson', totalInstalls: 30, serviceRequests: 2, performance: 93 }
      ];
      setInstallerPerformance(mockPerformance as unknown as InstallerPerformance[]);
    } catch (err: any) {
      console.error('Failed to fetch installer performance:', err);
    }
  }, []);

  useEffect(() => {
    fetchServiceRequests();
    fetchInstallerPerformance();
  }, [fetchServiceRequests, fetchInstallerPerformance]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, serviceRequest: ServiceRequest) => {
    setAnchorEl(event.currentTarget);
    setSelectedServiceRequest(serviceRequest);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedServiceRequest(null);
  };

  const handleViewDetails = (serviceRequest?: ServiceRequest) => {
    const request = serviceRequest || selectedServiceRequest;
    if (request) {
      // Navigate to service request details page
      navigate(`/services/${request._id}`);
    }
    handleMenuClose();
  };

  const handleEditServiceRequest = () => {
    if (selectedServiceRequest) {
      // Navigate to edit service request page
      navigate(`/services/${selectedServiceRequest._id}/edit`);
    }
    handleMenuClose();
  };

  const handleDeleteServiceRequest = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (selectedServiceRequest) {
      try {
        await mockApi.deleteServiceRequest(selectedServiceRequest._id);
        fetchServiceRequests();
        setDeleteDialogOpen(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete service request');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'default';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'on_hold': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Schedule />;
      case 'in_progress': return <Build />;
      case 'completed': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      case 'on_hold': return <Pause />;
      default: return <Schedule />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getIssueTypeColor = (issueType: string) => {
    switch (issueType) {
      case 'malfunction': return 'error';
      case 'warranty': return 'warning';
      case 'maintenance': return 'info';
      case 'upgrade': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPerformanceRating = (rate: number) => {
    if (rate <= 0.1) return { color: 'success', text: 'Excellent' };
    if (rate <= 0.2) return { color: 'info', text: 'Good' };
    if (rate <= 0.3) return { color: 'warning', text: 'Average' };
    return { color: 'error', text: 'Needs Improvement' };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#D4AF37', mb: 3 }}>
        Service Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Service Requests" />
        <Tab label="Installer Performance" />
      </Tabs>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search service requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="on_hold">On Hold</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={priorityFilter}
                      label="Priority"
                      onChange={(e) => setPriorityFilter(e.target.value)}
                    >
                      <MenuItem value="">All Priorities</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Issue Type</InputLabel>
                    <Select
                      value={issueTypeFilter}
                      label="Issue Type"
                      onChange={(e) => setIssueTypeFilter(e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="malfunction">Malfunction</MenuItem>
                      <MenuItem value="warranty">Warranty</MenuItem>
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                      <MenuItem value="upgrade">Upgrade</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('');
                      setPriorityFilter('');
                      setIssueTypeFilter('');
                      setInstallerFilter('');
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Service Request Cards */}
          <Grid container spacing={3}>
            {serviceRequests.map((request) => (
              <Grid item xs={12} sm={6} md={4} key={request._id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      backgroundColor: 'rgba(212, 175, 55, 0.05)',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease',
                    }
                  }}
                  onClick={() => handleViewDetails(request)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {request.customer.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {request.customer.contactPerson.firstName} {request.customer.contactPerson.lastName}
                          {request.customer.contactPerson.title && ` - ${request.customer.contactPerson.title}`}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, request);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {request.description}
                    </Typography>

                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                      <Chip
                        icon={getStatusIcon(request.status)}
                        label={request.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                      <Chip
                        label={request.priority.toUpperCase()}
                        color={getPriorityColor(request.priority) as any}
                        size="small"
                      />
                      <Chip
                        label={request.issueType.toUpperCase()}
                        color={getIssueTypeColor(request.issueType) as any}
                        size="small"
                      />
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Installer:</strong> {request.installer.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Technician:</strong> {request.assignedTechnician.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Reported:</strong> {formatDate(request.reportedDate)}
                      </Typography>
                      {request.scheduledDate && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Scheduled:</strong> {formatDate(request.scheduledDate)}
                        </Typography>
                      )}
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {request.warrantyCovered ? 'Warranty Covered' : 'Not Warranty'}
                      </Typography>
                      {request.customerSatisfaction && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            Satisfaction: {request.customerSatisfaction}/5
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {tabValue === 1 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: '#D4AF37', mb: 3 }}>
            Installer Performance Metrics
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Installer</TableCell>
                  <TableCell align="right">Installations</TableCell>
                  <TableCell align="right">Service Requests</TableCell>
                  <TableCell align="right">Service Rate</TableCell>
                  <TableCell align="right">Resolution Rate</TableCell>
                  <TableCell align="right">Avg Resolution Time</TableCell>
                  <TableCell align="right">Customer Satisfaction</TableCell>
                  <TableCell align="right">Performance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {installerPerformance.map((installer) => {
                  const performance = getPerformanceRating(installer.serviceRequestRate);
                  return (
                    <TableRow key={installer.installerId}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#D4AF37' }}>
                            <Person />
                          </Avatar>
                          {installer.installerName}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{installer.totalInstallations}</TableCell>
                      <TableCell align="right">{installer.totalServiceRequests}</TableCell>
                      <TableCell align="right">
                        {(installer.serviceRequestRate * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">
                        {(installer.resolutionEfficiency * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">
                        {installer.averageResolutionTime.toFixed(1)} days
                      </TableCell>
                      <TableCell align="right">
                        {installer.customerSatisfactionScore.toFixed(1)}/5
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={performance.text}
                          color={performance.color as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails()}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditServiceRequest}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteServiceRequest}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Service Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this service request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;
